import Hls, { ErrorData, HlsListeners } from 'hls.js'
import type { PlaybackError } from './PlaybackError'

/** Fatal errors to self-heal per kind; counters reset when a fragment loads. */
export const MAX_RECOVERY_ATTEMPTS = 2

// MediaError codes, inlined so this module never touches the DOM global
const MEDIA_ERR_NETWORK = 2
const MEDIA_ERR_DECODE = 3

export const toPlaybackError = (data: ErrorData): PlaybackError => ({
  code:
    data.type === Hls.ErrorTypes.MEDIA_ERROR
      ? MEDIA_ERR_DECODE
      : MEDIA_ERR_NETWORK,
  message: data.details,
  hlsError: {
    type: data.type,
    details: data.details,
    url: data.frag?.url ?? data.url,
    responseCode: data.response?.code,
  },
})

/**
 * Attempt the bounded remediation hls.js expects of the application for fatal
 * errors (it never retries them itself), and surface the failure once
 * recovery is exhausted. Returns a cleanup that detaches the listeners.
 */
export const attachHlsErrorRecovery = (
  hls: Hls,
  onFatalError: (error: PlaybackError) => void,
): (() => void) => {
  let networkRecoveries = 0
  let mediaRecoveries = 0
  let fragmentLoaded = false

  const handleFragLoaded = () => {
    fragmentLoaded = true
    networkRecoveries = 0
    mediaRecoveries = 0
  }

  const handleError: HlsListeners[typeof Hls.Events.ERROR] = (_, data) => {
    // eslint-disable-next-line no-console
    console.error('hls.js error', data)

    if (!data.fatal) return

    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
      // Cold-load fatals surface immediately — hls.js has already exhausted
      // its internal retries, so startLoad() rounds only delay the error UI.
      if (fragmentLoaded && networkRecoveries < MAX_RECOVERY_ATTEMPTS) {
        networkRecoveries += 1
        hls.startLoad()
        return
      }
    } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
      if (mediaRecoveries < MAX_RECOVERY_ATTEMPTS) {
        mediaRecoveries += 1
        // hls.js docs: swap the audio codec before the last recovery attempt
        if (mediaRecoveries === MAX_RECOVERY_ATTEMPTS) hls.swapAudioCodec()
        hls.recoverMediaError()
        return
      }
    }

    onFatalError(toPlaybackError(data))
  }

  hls.on(Hls.Events.ERROR, handleError)
  hls.on(Hls.Events.FRAG_LOADED, handleFragLoaded)

  return () => {
    hls.off(Hls.Events.ERROR, handleError)
    hls.off(Hls.Events.FRAG_LOADED, handleFragLoaded)
  }
}
